<script setup lang="ts">
    import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

    /**
     * The agent on the page as a collaborator, while it acts on it (spec
     * `page-tools-improvements.md`, M12): a cursor in its color with its name
     * and the name of what it acts on, as in a shared editor, and a ring on
     * that element, drawn as the system draws keyboard focus. The cursor
     * leaves the chat, glides to the element and clicks; the ring settles
     * there at the click, and on the next element at the next. A layer of its own
     * over the page, its dialogs too, letting every click through.
     * Decoration only, hidden from assistive technology: the chat says what
     * the agent does.
     */
    const props = defineProps<{
        /** Shown while true: the agent controls the page. */
        active: boolean
        /**
         * The element the agent acts on, and its name; the last one between
         * two actions. `heading`: the next one, before its action, which the
         * cursor glides to without clicking.
         */
        target?: { element: Element; name: string; heading?: boolean }
        /** The agent's name, beside the cursor. */
        agentName?: string
        /** The chat, where the cursor comes from and goes back to. */
        home?: () => Element | null | undefined
        /** A dialog of the page the layer stays above: it opens over everything shown before it. */
        above?: Element | null
    }>()

    const emit = defineEmits<{
        /** The action can run: the cursor clicked on the element, or (`false`) it has nothing to show. */
        arrived: [clicked: boolean]
    }>()

    type Values = number[]
    type Box = { x: number; y: number; width: number; height: number }

    const layer = ref<HTMLElement>()
    const cursor = ref<HTMLElement>()
    const ring = ref<HTMLElement>()
    const tagLabel = ref<HTMLElement>()
    const cursorShown = ref(false)
    const ringShown = ref(false)
    /** A new value replays the click, the next the ring's settling. */
    const clicks = ref(0)
    const settles = ref(0)
    const actionName = ref('')

    const reducedMotion = () =>
        typeof matchMedia === 'function' &&
        matchMedia('(prefers-reduced-motion: reduce)').matches

    /**
     * Critically damped springs, one per value (Apple's damping 1): no
     * overshoot, and a new goal starts from where the value is and how fast
     * it moves, never from where the last one was going. `response` is the
     * seconds it takes to get there.
     */
    const spring = (response: number, apply: (values: Values) => void) => {
        const omega = (2 * Math.PI) / response
        let position: Values | undefined
        let velocity: Values = []
        let goal: Values = []
        let frame = 0
        let last = 0
        let reached: (() => void) | undefined
        const tick = (now: number) => {
            const step = Math.min(1 / 30, (now - last) / 1000)
            last = now
            let farthest = 0
            let moving = false
            goal.forEach((target, index) => {
                const offset = position![index]! - target
                velocity[index] =
                    velocity[index]! +
                    (-omega * omega * offset - 2 * omega * velocity[index]!) *
                        step
                position![index] = position![index]! + velocity[index]! * step
                const left = Math.abs(position![index]! - target)
                farthest = Math.max(farthest, left)
                moving ||= left > 0.5 || Math.abs(velocity[index]!) > 1
            })
            if (!moving) {
                position = [...goal]
            }
            apply(position!)
            if (reached && farthest < 2) {
                const done = reached
                reached = undefined
                done()
            }
            frame = moving ? requestAnimationFrame(tick) : 0
        }
        const jump = (values: Values) => {
            cancelAnimationFrame(frame)
            frame = 0
            position = [...values]
            velocity = values.map(() => 0)
            goal = [...values]
            apply(position)
        }
        return {
            jump,
            /** Glides to `values`, calling `onReach` once there. */
            to(values: Values, onReach?: () => void) {
                if (!position || reducedMotion()) {
                    jump(values)
                    onReach?.()
                    return
                }
                goal = [...values]
                reached = onReach
                if (!frame) {
                    last = performance.now()
                    frame = requestAnimationFrame(tick)
                }
            },
            /** A new place for the same goal, as the page scrolls: kept to at once when at rest. */
            follow(values: Values) {
                if (frame) {
                    goal = [...values]
                } else if (position) {
                    jump(values)
                }
            },
            stop() {
                cancelAnimationFrame(frame)
                frame = 0
                reached = undefined
            },
        }
    }

    const cursorSpring = spring(0.45, ([x, y]) => {
        cursor.value?.style.setProperty('translate', `${x}px ${y}px`)
    })
    /** The ring over a box: it moves at once, as focus does, never glides. */
    const ringAt = ([x, y, width, height]: Values) => {
        const element = ring.value
        if (element) {
            element.style.translate = `${x}px ${y}px`
            element.style.width = `${width}px`
            element.style.height = `${height}px`
        }
    }

    const boxOf = (element: Element | null | undefined): Box | undefined => {
        if (!element?.isConnected) {
            return undefined
        }
        const { left, top, width, height } = element.getBoundingClientRect()
        return width || height ? { x: left, y: top, width, height } : undefined
    }
    /**
     * The part of a box beside the chat: a field that runs under it is
     * clicked where the user sees it, and the tag stays out from under the
     * chat. The wider side of the two, the whole box when none is left.
     */
    const besideChat = (box: Box): Box => {
        const chat = boxOf(props.home?.())
        if (
            !chat ||
            box.y >= chat.y + chat.height ||
            box.y + box.height <= chat.y
        ) {
            return box
        }
        const right = box.x + box.width
        const before = Math.min(right, chat.x) - box.x
        const after = right - Math.max(box.x, chat.x + chat.width)
        if (before <= 0 && after <= 0) {
            return box
        }
        return before >= after
            ? { ...box, width: before }
            : { ...box, x: right - after, width: after }
    }
    /** The tag's `left` from the cursor's tip in the styles, and a gap after it. */
    const TAG_OFFSET = 13
    const TAG_GAP = 8
    // Where the cursor clicks: toward the end of a field's text, the middle
    // of a small control, and early enough on the field that its tag ends
    // before the chat
    const pointIn = (whole: Box): Values => {
        const box = besideChat(whole)
        let x = box.x + box.width * (box.width > 80 ? 0.72 : 0.5)
        const y = box.y + box.height * (box.height > 24 ? 0.62 : 0.5)
        const chat = boxOf(props.home?.())
        const tag = tagLabel.value?.offsetWidth ?? 0
        if (chat && x < chat.x && y < chat.y + chat.height && y + 48 > chat.y) {
            x = Math.max(
                box.x + Math.min(TAG_GAP, box.width / 2),
                Math.min(x, chat.x - TAG_OFFSET - tag - TAG_GAP),
            )
        }
        return [x, y]
    }
    const RING_GAP = 3
    const ringAround = (box: Box): Values => [
        box.x - RING_GAP,
        box.y - RING_GAP,
        box.width + RING_GAP * 2,
        box.height + RING_GAP * 2,
    ]
    const homePoint = (): Values | undefined => {
        const box = boxOf(props.home?.())
        return box ? [box.x + 24, box.y + box.height / 2] : undefined
    }

    /**
     * Never over the chat: the ring of a field that runs under it would frame
     * the chat's own content. The layer leaves the chat's box out, corners
     * included, and the cursor comes out from under its edge.
     */
    const clipChat = () => {
        const element = layer.value
        const chat = props.home?.()
        const box = boxOf(chat)
        if (!element) {
            return
        }
        if (!chat || !box) {
            element.style.removeProperty('clip-path')
            return
        }
        const { x, y, width: w, height: h } = box
        const r = Math.min(
            parseFloat(getComputedStyle(chat).borderTopLeftRadius) || 0,
            w / 2,
            h / 2,
        )
        const arc = (toX: number, toY: number) =>
            r ? `A${r} ${r} 0 0 1 ${toX} ${toY}` : `L${toX} ${toY}`
        element.style.clipPath =
            `path(evenodd, "M0 0H${window.innerWidth}V${window.innerHeight}H0Z ` +
            `M${x + r} ${y}H${x + w - r}${arc(x + w, y + r)}` +
            `V${y + h - r}${arc(x + w - r, y + h)}` +
            `H${x + r}${arc(x, y + h - r)}V${y + r}${arc(x + r, y)}Z")`
    }

    /** Over everything shown before it, the page's open dialogs too. */
    const raise = () => {
        const element = layer.value
        try {
            if (element?.matches(':popover-open')) {
                element.hidePopover()
            }
            element?.showPopover()
        } catch {
            // No popover in this browser: the layer's z-index holds
        }
    }
    let lowering: ReturnType<typeof setTimeout> | undefined
    const lower = () => {
        clearTimeout(lowering)
        lowering = setTimeout(() => {
            try {
                layer.value?.hidePopover()
            } catch {
                // Never shown
            }
        }, 450)
    }

    const inView = (box: Box) =>
        box.y + box.height > 0 &&
        box.y < window.innerHeight &&
        box.x + box.width > 0 &&
        box.x < window.innerWidth

    /** The element the cursor goes to or stays on. */
    let cursorOn: Element | undefined
    /** The element the ring frames: the last the cursor clicked on, not the one it heads for. */
    let ringOn: Element | undefined
    const placeRing = (element: Element) => {
        const box = boxOf(element)
        if (!box) {
            ringShown.value = false
            return
        }
        ringOn = element
        const radius = parseFloat(getComputedStyle(element).borderTopLeftRadius)
        ring.value?.style.setProperty(
            '--pk-agent-presence-radius',
            `${(Number.isFinite(radius) ? radius : 0) + RING_GAP}px`,
        )
        // On the element at the click, as focus moves: gliding from the last
        // one, it lagged the cursor that had already clicked (Alessandro,
        // 2026-10-28)
        ringAt(ringAround(box))
        ringShown.value = true
        settles.value++
    }

    /** The latest show: an older one still waiting for the tag gives way. */
    let showing = 0
    const show = async (target: {
        element: Element
        name: string
        heading?: boolean
    }) => {
        const mine = ++showing
        // Nothing waits on a heading: it says nothing back
        const arrived = (clicked: boolean) =>
            target.heading || emit('arrived', clicked)
        const first = boxOf(target.element)
        if (!props.active || !first) {
            arrived(false)
            return
        }
        // Out of view, the next element waits for its action, which
        // scrolls to it first
        if (target.heading && !(cursorShown.value && inView(first))) {
            return
        }
        clearTimeout(lowering)
        clipChat()
        // A label's own colon ("Name:") reads as a stray mark in the tag
        actionName.value = target.name.replace(/\s*[:：]\s*$/, '')
        // The tag at its new width, for a point that keeps it beside the chat
        await nextTick()
        if (mine !== showing) {
            return
        }
        const box = boxOf(target.element)
        if (!props.active || !box) {
            arrived(false)
            return
        }
        const point = pointIn(box)
        cursorOn = target.element
        if (target.heading) {
            cursorSpring.to(point)
            return
        }
        if (!cursorShown.value) {
            cursorSpring.jump(homePoint() ?? [point[0]! + 40, point[1]! + 24])
            cursorShown.value = true
        }
        cursorSpring.to(point, () => {
            clicks.value++
            placeRing(target.element)
            arrived(true)
        })
    }

    // Back into the chat, then gone
    const leave = () => {
        ringShown.value = false
        cursorOn = undefined
        ringOn = undefined
        actionName.value = ''
        const back = homePoint()
        const gone = () => {
            cursorShown.value = false
            lower()
        }
        if (cursorShown.value && back) {
            cursorSpring.to(back, gone)
        } else {
            gone()
        }
    }

    watch(
        () => props.active,
        (active) => {
            if (!active) {
                leave()
                return
            }
            raise()
            if (props.target) {
                show(props.target)
            }
        },
    )
    watch(
        () => props.target,
        (target) => {
            if (target) {
                show(target)
            }
        },
    )
    watch(
        () => props.above,
        (dialog) => {
            if (dialog && props.active) {
                raise()
            }
        },
    )

    // Glued to the element while the page scrolls or the window changes
    let following = 0
    const follow = () => {
        if (following) {
            return
        }
        following = requestAnimationFrame(() => {
            following = 0
            if (!props.active) {
                return
            }
            clipChat()
            const box = boxOf(cursorOn)
            if (box) {
                cursorSpring.follow(pointIn(box))
            }
            // The ring stays on its element until the cursor clicks on the
            // next one. Taken away by the page (the option of a list that
            // closed at the click, seen on demoqa), it would frame what is
            // under it now
            const framed = ringShown.value ? boxOf(ringOn) : undefined
            if (framed) {
                ringAt(ringAround(framed))
            } else {
                ringShown.value = false
            }
        })
    }
    // And while the page moves or removes it, which no event tells, or the
    // chat moves under the cut the layer makes for it
    const WATCH_MS = 150
    let watching: ReturnType<typeof setInterval> | undefined
    watch(cursorShown, (shown) => {
        clearInterval(watching)
        watching = shown ? setInterval(follow, WATCH_MS) : undefined
    })
    onMounted(() => {
        window.addEventListener('scroll', follow, {
            capture: true,
            passive: true,
        })
        window.addEventListener('resize', follow, { passive: true })
        if (props.active) {
            raise()
            if (props.target) {
                show(props.target)
            }
        }
    })
    onBeforeUnmount(() => {
        window.removeEventListener('scroll', follow, { capture: true })
        window.removeEventListener('resize', follow)
        cancelAnimationFrame(following)
        clearInterval(watching)
        clearTimeout(lowering)
        cursorSpring.stop()
    })
</script>

<template>
    <div
        ref="layer"
        class="pk-agent-presence"
        popover="manual"
        aria-hidden="true">
        <span
            ref="ring"
            class="pk-agent-presence__ring"
            :class="{ 'pk-agent-presence__ring--shown': ringShown }">
            <span :key="settles" class="pk-agent-presence__ring-box">
                <span class="pk-agent-presence__halo" />
                <span class="pk-agent-presence__edge" />
            </span>
        </span>
        <span
            ref="cursor"
            class="pk-agent-presence__cursor"
            :class="{ 'pk-agent-presence__cursor--shown': cursorShown }">
            <svg
                :key="clicks"
                class="pk-agent-presence__arrow"
                :class="{ 'pk-agent-presence__arrow--click': clicks }"
                width="20"
                height="22"
                viewBox="0 0 20 22">
                <path
                    d="M2 1.5 L2 17.5 L6.4 13.6 L9.3 20.2 L12.4 18.9 L9.5 12.4 L15.6 12.1 Z" />
            </svg>
            <span ref="tagLabel" class="pk-agent-presence__label">
                <span>{{ agentName }}</span>
                <span
                    v-if="actionName"
                    :key="actionName"
                    class="pk-agent-presence__action">
                    · {{ actionName }}
                </span>
            </span>
        </span>
    </div>
</template>

<style lang="scss">
    // The cursor and the ring move with `translate` set from the springs,
    // the rest with transforms and opacities only. The agent's color comes
    // from `--chatbot-main-color` in the widget, the brand elsewhere.
    .pk-agent-presence {
        --pk-agent-presence-color: var(
            --chatbot-main-color,
            var(--color-brand)
        );
        --pk-agent-presence-ink: var(--chatbot-contrast-color, #fff);
        --pk-agent-presence-tone-before: var(--pk-agent-presence-color);
        --pk-agent-presence-tone-after: var(--pk-agent-presence-color);

        // A popover takes the top layer: the user agent's box goes, the
        // layer covers the window and lets every click through
        position: fixed;
        inset: 0;
        z-index: 2147483646;
        width: auto;
        height: auto;
        margin: 0;
        padding: 0;
        border: 0;
        overflow: visible;
        background: transparent;
        color: inherit;
        pointer-events: none;

        @supports (color: oklch(from red l c h)) {
            --pk-agent-presence-tone-before: oklch(
                from var(--pk-agent-presence-color) l c calc(h - 50)
            );
            --pk-agent-presence-tone-after: oklch(
                from var(--pk-agent-presence-color) l c calc(h + 50)
            );
        }

        // Its origin is the window's corner, `translate` takes it there
        &__ring,
        &__cursor {
            position: absolute;
            top: 0;
            left: 0;
            opacity: 0;
            transition: opacity var(--duration-300) ease-out;
        }

        &__ring--shown,
        &__cursor--shown {
            opacity: 1;
        }

        &__ring {
            // The light inside reads the ring's size in `cqw` and `cqh`
            container-type: size;
        }

        // Settles onto the element from a little larger, as focus does
        &__ring-box {
            position: absolute;
            inset: 0;
            border-radius: var(--pk-agent-presence-radius, 0);
            animation: pk-agent-presence-settle 0.36s
                cubic-bezier(0.23, 1, 0.32, 1);
        }

        &__halo {
            position: absolute;
            inset: 0;
            border-radius: inherit;
            box-shadow:
                0 0 0 4px
                    color-mix(
                        in srgb,
                        var(--pk-agent-presence-color) 16%,
                        transparent
                    ),
                0 0 20px
                    color-mix(
                        in srgb,
                        var(--pk-agent-presence-color) 22%,
                        transparent
                    );
        }

        // Two pixels of the agent's colors turning slowly: the content box
        // is cut out
        &__edge {
            position: absolute;
            inset: 0;
            border-radius: inherit;
            padding: 2px;
            overflow: hidden;
            mask:
                linear-gradient(#000 0 0) content-box,
                linear-gradient(#000 0 0);
            mask-composite: exclude;
            -webkit-mask-composite: xor;

            &::before {
                content: '';
                position: absolute;
                top: 50%;
                left: 50%;
                width: calc(100cqw + 100cqh);
                aspect-ratio: 1;
                background: conic-gradient(
                    var(--pk-agent-presence-color),
                    var(--pk-agent-presence-tone-after),
                    var(--pk-agent-presence-color),
                    var(--pk-agent-presence-tone-before),
                    var(--pk-agent-presence-color)
                );
                transform: translate(-50%, -50%);
                animation: pk-agent-presence-turn 6s linear infinite;
            }
        }

        // The tip of the arrow is the point it clicks
        &__cursor {
            margin: -1px 0 0 -2px;
            will-change: translate;
        }

        &__arrow {
            display: block;
            transform-origin: 2px 1px;
            filter: drop-shadow(0 1px 2px rgb(0 0 0 / 28%));

            path {
                fill: var(--pk-agent-presence-color);
                stroke: #fff;
                stroke-width: 1.5;
                stroke-linejoin: round;
            }

            &--click {
                animation: pk-agent-presence-click 0.24s
                    cubic-bezier(0.23, 1, 0.32, 1);
            }
        }

        // The agent's name and what it acts on, as a collaborator's tag
        &__label {
            position: absolute;
            top: 19px;
            left: 13px;
            display: inline-flex;
            align-items: baseline;
            gap: var(--spacing-6);
            max-width: 280px;
            padding: var(--spacing-3) var(--spacing-8);
            border-radius: 3px 9px 9px;
            background: var(--pk-agent-presence-color);
            box-shadow: 0 2px 8px rgb(0 0 0 / 16%);
            color: var(--pk-agent-presence-ink);
            font-family: inherit;
            font-size: 12px;
            font-weight: 500;
            line-height: 1.4;
            letter-spacing: 0.01em;
            white-space: nowrap;
        }

        &__action {
            overflow: hidden;
            text-overflow: ellipsis;
            font-weight: 400;
            opacity: 0.85;
            animation: pk-agent-presence-swap 0.26s
                cubic-bezier(0.23, 1, 0.32, 1);
        }

        // The signal stays, the motion goes: the springs jump, nothing turns
        @media (prefers-reduced-motion: reduce) {
            &__ring-box,
            &__arrow--click,
            &__action,
            &__edge::before {
                animation: none;
            }
        }
    }

    @keyframes pk-agent-presence-settle {
        from {
            opacity: 0;
            scale: 1.06;
        }
    }

    @keyframes pk-agent-presence-click {
        40% {
            scale: 0.86;
        }
    }

    @keyframes pk-agent-presence-swap {
        from {
            opacity: 0;
            filter: blur(3px);
        }
    }

    @keyframes pk-agent-presence-turn {
        to {
            transform: translate(-50%, -50%) rotate(1turn);
        }
    }
</style>
